/**
 * What a checkout start is worth to the ad platforms.
 *
 * Deliberately different from the number the checkout page displays. The page
 * must keep showing "5 monthly payments of $397" because that is what the
 * customer pays. Google, Meta and GA4 need the commitment, $1,985, because
 * Smart Bidding ranks conversions by value and otherwise cannot tell a $1,997
 * Bootcamp cart from a $99 subscription cart.
 *
 * THE BUG THIS REPLACES, found 2026-09-18. CheckoutPage sent
 * `(totals.recurringCents + totals.oneTimeCents) / 100`. `recurringCents` is
 * the PER PERIOD amount, so a five payment Bootcamp reported 397. The starter
 * page sent amount due today, which is 0 during a trial. Google was also
 * discarding whatever arrived, because the Begin checkout conversion action had
 * `always_use_default_value` set with a flat $1. That override is off now, so
 * these numbers actually reach the auction.
 */

/**
 * Open-ended subscriptions use only the amount actually collected at checkout.
 * A future predicted-LTV value belongs here only after observed retention
 * supports a documented model. Fixed installment contracts remain different:
 * the buyer has accepted a finite total, so every scheduled installment counts.
 */
export const SUBSCRIPTION_VALUE_MONTHS = 1;

type MaybePlan = {
  amount?: number | null;
  discountedAmount?: number | null;
  interval?: string | null;
  metadata?: { installments?: unknown } | null;
} | null | undefined;

type CartLine = {
  plan?: MaybePlan;
  raw?: { quantity?: number | null } | null;
  product?: { metadata?: { isRecurring?: boolean } | null } | null;
} | null | undefined;

/**
 * Installments only count when the plan really is a fixed series. Anything
 * else, including a malformed value or a count of 1, falls through to the
 * subscription assumption rather than silently multiplying by one.
 */
const readInstallmentCount = (plan: MaybePlan): number | null => {
  const parsed = Number(plan?.metadata?.installments);
  return Number.isInteger(parsed) && parsed > 1 ? parsed : null;
};

/** Total cents an ad platform should attribute to this cart. */
export const checkoutAnalyticsValueCents = (items: CartLine[] | undefined | null): number => {
  if (!Array.isArray(items)) return 0;

  return items.reduce<number>((total, item) => {
    const plan = item?.plan;
    const unit = plan?.discountedAmount ?? plan?.amount;
    if (typeof unit !== 'number' || !Number.isFinite(unit)) {
      return total; // catalog has not resolved this line yet
    }

    const quantity = item?.raw?.quantity ?? 1;
    const lineToday = unit * quantity;

    // A one-time purchase is already the whole commitment.
    if (!plan?.interval || item?.product?.metadata?.isRecurring === false) {
      return total + lineToday;
    }

    // A fixed installment series is worth every installment.
    const installments = readInstallmentCount(plan);
    if (installments) {
      return total + lineToday * installments;
    }

    // Do not invent LTV for an open-ended subscription.
    return total + lineToday * SUBSCRIPTION_VALUE_MONTHS;
  }, 0);
};

/** Same value in whole currency units, which is what gtag and fbq expect. */
export const checkoutAnalyticsValue = (items: CartLine[] | undefined | null): number =>
  checkoutAnalyticsValueCents(items) / 100;
